import { z } from "zod";
import { competenciaSchema, dataSchema, somarMeses, textoParaData } from "./competencia";
import { FORMAS_DE_PAGAMENTO } from "./lancamento";

/** Valores de um cliente num mês: faturado (serviços concluídos) ou recebido (recebimentos). */
export type ValorDoCliente = { cliente: string; competencia: string; valorCents: number };

export type SaldoDoCliente = {
  cliente: string;
  competencia: string;
  faturadoCents: number;
  recebidoCents: number;
  /** Dado como perdido (abate o saldo, não é dinheiro). */
  perdidoCents: number;
  saldoCents: number;
};

export function saldosPorClienteEMes(faturado: ValorDoCliente[], recebido: ValorDoCliente[], perdido: ValorDoCliente[] = []): SaldoDoCliente[] {
  const mapa = new Map<string, SaldoDoCliente>();
  const pegar = (v: ValorDoCliente) => {
    const chave = `${v.cliente}|${v.competencia}`;
    let s = mapa.get(chave);
    if (!s) mapa.set(chave, (s = { cliente: v.cliente, competencia: v.competencia, faturadoCents: 0, recebidoCents: 0, perdidoCents: 0, saldoCents: 0 }));
    return s;
  };
  for (const f of faturado) pegar(f).faturadoCents += f.valorCents;
  for (const r of recebido) pegar(r).recebidoCents += r.valorCents;
  for (const p of perdido) pegar(p).perdidoCents += p.valorCents;
  for (const s of mapa.values()) s.saldoCents = s.faturadoCents - s.recebidoCents - s.perdidoCents;
  return [...mapa.values()];
}

export type SituacaoDoCliente = "QUITADO" | "PARCIAL" | "EM_ABERTO" | "CREDITO" | "PERDIDO";

export function situacaoDoCliente(s: { faturadoCents: number; recebidoCents: number; perdidoCents?: number }): SituacaoDoCliente {
  const perdido = s.perdidoCents ?? 0;
  if (s.recebidoCents > s.faturadoCents) return "CREDITO";
  if (s.recebidoCents + perdido >= s.faturadoCents) return perdido > 0 ? "PERDIDO" : "QUITADO";
  return s.recebidoCents > 0 ? "PARCIAL" : "EM_ABERTO";
}

export const SITUACAO_DO_CLIENTE_LABELS: Record<SituacaoDoCliente, string> = {
  QUITADO: "Quitado",
  PERDIDO: "Dado como perdido",
  PARCIAL: "Parcial",
  EM_ABERTO: "Em aberto",
  CREDITO: "Pagou a mais",
};

export type IdadeDoSaldo = "ATE_30" | "DE_31_A_60" | "MAIS_DE_60";

export const IDADE_LABELS: Record<IdadeDoSaldo, string> = {
  ATE_30: "Até 30 dias",
  DE_31_A_60: "31 a 60 dias",
  MAIS_DE_60: "Mais de 60 dias",
};

/** Dias desde o fim do mês faturado (o extrato fecha no último dia) até hoje. */
export function diasEmAberto(competencia: string, hoje: string): number {
  const fimDoMes = textoParaData(`${somarMeses(competencia, 1)}-01`).getTime() - 86_400_000;
  return Math.max(0, Math.round((textoParaData(hoje).getTime() - fimDoMes) / 86_400_000));
}

export function idadeDoSaldo(dias: number): IdadeDoSaldo {
  if (dias <= 30) return "ATE_30";
  return dias <= 60 ? "DE_31_A_60" : "MAIS_DE_60";
}

export const registrarRecebimentoSchema = z.object({
  clienteId: z.string().min(1),
  competencia: competenciaSchema,
  valorCents: z.number().int().positive("Informe o valor"),
  pagoEm: dataSchema,
  formaDePagamento: z.enum(FORMAS_DE_PAGAMENTO).nullable().optional(),
  observacao: z.string().trim().max(500).nullable().optional(),
});
export type RegistrarRecebimentoInput = z.infer<typeof registrarRecebimentoSchema>;

const motivoDaPerda = z.string().trim().min(3, "Diga o motivo").max(300);

/** Saldo de um mês (fluxo de antes dos extratos) dado como perdido. */
export const perdaDoMesSchema = z.object({ clienteId: z.string().min(1), competencia: competenciaSchema, valorCents: z.number().int().positive("Informe o valor"), motivo: motivoDaPerda });
export const perdaDoExtratoSchema = z.object({ extratoId: z.string().min(1), valorCents: z.number().int().positive("Informe o valor"), motivo: motivoDaPerda });
